import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiCalendar,
  FiTag,
  FiDownload,
  FiPaperclip,
  FiFileText
} from 'react-icons/fi';
import PageWrapper from '../../components/PageWrapper';
import { fetchNoticeDetail, fetchNoticeList } from '../../api/notice';
import {
  extractAttachmentsFromHtml,
  formatNoticeDate,
  mapNoticeListItem,
  rewriteCmsHtmlResources
} from '../../utils/noticeHelper';
import './NoticeDetail.css';

const NoticeDetail = () => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [notice, setNotice] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [slug]);

  useEffect(() => {
    let alive = true;
    const contentId = slug;

    const load = async () => {
      setLoading(true);
      setError('');
      setNotice(null);

      try {
        let catalogId = location.state?.catalogId;
        let listItem = null;

        const listData = await fetchNoticeList();
        const rows = (listData?.rows || []).map(mapNoticeListItem);
        if (!alive) return;

        listItem = rows.find((item) => String(item.contentId) === String(contentId));
        if (!catalogId) {
          catalogId = listItem?.catalogId;
        }

        setRelated(
          rows
            .filter((item) => String(item.contentId) !== String(contentId))
            .slice(0, 4)
        );

        if (!catalogId) {
          throw new Error('NOT_FOUND');
        }

        const detail = await fetchNoticeDetail(catalogId, contentId);
        if (!alive) return;

        const contentHtml = rewriteCmsHtmlResources(detail?.contentHtml || '');
        setNotice({
          contentId,
          catalogId,
          title: detail?.title || listItem?.title || location.state?.title || '',
          category:
            detail?.catalogName ||
            listItem?.category ||
            location.state?.category ||
            '通知公告',
          date: formatNoticeDate(
            detail?.publishDate || listItem?.date || location.state?.date
          ),
          contentHtml,
          attachments: extractAttachmentsFromHtml(contentHtml),
        });
      } catch (e) {
        if (!alive) return;
        setError(
          e?.message === 'NOT_FOUND'
            ? '公告不存在或已下线'
            : '公告详情加载失败，请稍后重试'
        );
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [slug, location.state]);

  const attachments = useMemo(() => notice?.attachments || [], [notice]);

  if (loading) {
    return (
      <PageWrapper className="notice-detail-page" instant>
        <div className="notice-not-found">
          <div className="container">
            <h1>正在加载...</h1>
            <p>请稍候</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (error || !notice) {
    return (
      <PageWrapper className="notice-detail-page" instant>
        <div className="notice-not-found">
          <div className="container">
            <h1>公告不存在</h1>
            <p>{error || '您访问的通知公告可能已下线或链接有误。'}</p>
            <Link to="/notices" className="btn-back-list">
              返回公告列表
            </Link>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper className="notice-detail-page" instant>
      <section className="notice-detail-hero">
        <div className="container">
          <button
            type="button"
            className="back-btn"
            onClick={() => navigate('/notices')}
          >
            <FiArrowLeft />
            返回公告列表
          </button>
          <div className="notice-detail-tags">
            <span className="tag">
              <FiTag />
              {notice.category}
            </span>
            {notice.date && (
              <span className="tag date">
                <FiCalendar />
                {notice.date}
              </span>
            )}
          </div>
          <h1>{notice.title}</h1>
        </div>
      </section>

      <section className="notice-detail-body">
        <div className="container">
          {notice.contentHtml && (
            <article
              className="notice-article notice-html"
              dangerouslySetInnerHTML={{ __html: notice.contentHtml }}
            />
          )}

          {/* {attachments.length > 0 && (
            <div className="notice-attachment">
              <div className="attachment-header">
                <h2>
                  <FiPaperclip />
                  附件下载
                </h2>
              </div>
              <ul className="attachment-list">
                {attachments.map((file) => (
                  <li key={file.url}>
                    <div className="attachment-file">
                      <FiFileText />
                      <div>
                        <strong>{file.name}</strong>
                        <span>{file.ext.toUpperCase()} 文件</span>
                      </div>
                    </div>
                    <a
                      className="attachment-download"
                      href={file.url}
                      download={file.name}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FiDownload />
                      下载
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )} */}

          {related.length > 0 && (
            <aside className="notice-related">
              <h2>更多公告</h2>
              <ul>
                {related.map((item) => (
                  <li key={item.contentId}>
                    <Link
                      to={`/notices/${item.slug}`}
                      state={{
                        catalogId: item.catalogId,
                        title: item.title,
                        category: item.category,
                        date: item.date,
                      }}
                    >
                      <span className="related-meta">
                        <span className="related-category">{item.category}</span>
                        {item.date && (
                          <span className="related-date">{item.date}</span>
                        )}
                      </span>
                      <span className="related-title">{item.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>
          )}
        </div>
      </section>
    </PageWrapper>
  );
};

export default NoticeDetail;
